import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Form,
  FormInstance,
  Input,
  Popconfirm,
  Radio,
  Row,
  Select,
  Switch,
  Typography,
  message, Modal} from 'antd';
import CustomInputNumber from 'components/CustomInputNumber';
import { LANGUAGE_OPTIONS } from 'constants/index';
import { contractService } from 'content/service';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { companyApiService } from 'service/api/companyApiService';
import { CompanyInterface } from 'type/companyType';
import * as Component from '../../../../../../shared/components';
import ContentsMapping from './ContentsMapping';

const { Option } = Select;

interface ContractDetailProps {
  form: FormInstance;
  contractId: number;
  termList: { id: number; title: string }[];
  onTermListRefresh?: () => Promise<void>;
}
const paymentTypeOptions = ['RS_ONLY', 'FLAT', 'FLAT_CREDIT', 'MG_RS', 'FREE'];
const taxTypeOptions = ['NET', 'GROSS'];
// Options selectable in dropdown
const activePlatformsOptions = ['ODK', 'ODC', 'ODV', 'AMASIAN', 'THIRD_PARTY_CHANNEL'];
// Deprecated options that may exist in existing data (not selectable, display only)
const deprecatedPlatformsOptions = ['LG', 'DISTRIBUTION', 'THEATER'];

// Options selectable in dropdown
const activeSalesTypeOptions = [
  'AVOD',
  'SVOD',
  'PPV',
  'FAST',
  'THIRD_PARTY_AVOD',
  'THIRD_PARTY_FAST',
  'THIRD_PARTY_SVOD',
  'THIRD_PARTY_PPV',
  'THEATER',
  'CTV',
];
// Deprecated options that may exist in existing data (not selectable, display only)
const deprecatedSalesTypeOptions = ['PPV/TVOD'];
const titleTypeOptions = ['SINGLE_TITLE', 'MULTI_TITLE', 'CHANNEL'];
const reportFrequencyOptions = ['MONTHLY', 'QUARTERLY'];
const reportDueDaysOptions = [15, 30, 45];
const paymentTermsOptions = ['NET_30', 'NET_45', 'NET_60'];
const salesTypeDisplayNames: Record<string, string> = {
  AVOD: 'AVOD',
  SVOD: 'SVOD',
  PPV: 'PPV',
  FAST: 'FAST',
  THIRD_PARTY_AVOD: '3rd party AVOD',
  THIRD_PARTY_FAST: '3rd party FAST',
  THIRD_PARTY_SVOD: '3rd party SVOD',
  THIRD_PARTY_PPV: '3rd party PPV',
  THEATER: 'THEATER',
  CTV: 'CTV',
  'PPV/TVOD': 'PPV/TVOD',
};
const servicePeriodTypeOptions = [
  'CONTRACT_PERIOD_BASED',
  'PUBLISHED_DATE_BASED',
  'DELIVERED_DATE_BASED',
];

const renderCompanyOption = (company: CompanyInterface) => {
  const titlePart = company.title_en ? `${company.title} (${company.title_en})` : company.title;
  const countryPart = company.country ? ` / ${company.country}` : '';
  const label = titlePart + countryPart;

  return (
    <Option key={company.id} value={company.id} title={label}>
      <span style={{ fontWeight: 500 }}>{label}</span>
    </Option>
  );
};

const TermDetail: React.FC<ContractDetailProps> = ({
  form,
  contractId,
  termList,
  onTermListRefresh,
}) => {
  const [selectedTermId, setSelectedTermId] = useState<number | null>(null);
  const [isNewTerm, setIsNewTerm] = useState<boolean>(false);
  const paymentType = Form.useWatch('payment_type', form);
  const currentPlatforms: string[] = Form.useWatch('platforms', form) || [];
  const currentSalesTypes: string[] = Form.useWatch('sales_types', form) || [];
  const [searchCompanies, setSearchCompanies] = useState<CompanyInterface[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Include deprecated values in options if they exist in current data (for display)
  const platformsOptions = [
    ...activePlatformsOptions,
    ...deprecatedPlatformsOptions.filter(opt => currentPlatforms.includes(opt)),
  ];
  const salesTypeOptions = [
    ...activeSalesTypeOptions,
    ...deprecatedSalesTypeOptions.filter(opt => currentSalesTypes.includes(opt)),
  ];

  const handleAddNewTerm = useCallback(
    (showMessage = false) => {
      console.log('🚀 handleAddNewTerm called with showMessage:', showMessage);

      // Generate new term ID (using negative number as temporary ID)
      const newTermId = -Date.now();
      console.log('🆔 Generated newTermId:', newTermId);

      // Reset form first
      form.resetFields();
      console.log('🧹 Form reset completed');

      // Set default values for new term (calculated directly)
      const termNumber = termList.length + 1;
      const newTermData = {
        title: `New Term ${termNumber}`,
        title_en: `New Term ${termNumber}`,
        payment_type: 'RS_ONLY',
        title_type: 'SINGLE_TITLE',
        service_period_type: 'CONTRACT_PERIOD_BASED',
      };
      console.log('📋 New term data prepared:', newTermData);

      // Update state first
      setSelectedTermId(newTermId);
      setIsNewTerm(true);
      console.log('🔄 State updated - selectedTermId:', newTermId, 'isNewTerm: true');

      // Set form values after state update (runs in next render cycle via setTimeout)
      setTimeout(() => {
        console.log('⏰ Setting form values after state update');
        form.setFieldsValue(newTermData);
        console.log('✅ Form values set:', form.getFieldsValue());
      }, 0);

      // Show message only when button is clicked
      if (showMessage) {
        message.success('A new Term has been created. Please fill in the details and click Save.');
      }
    },
    [form, termList.length]
  );

  const debouncedSearchCompanies = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        setSearchCompanies([]);
        return;
      }

      setSearchLoading(true);
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('search', searchTerm);
        queryParams.append('page_size', '50');

        const result = await companyApiService.fetch(queryParams);
        setSearchCompanies(result.results);
      } catch (error) {
        console.error('Company search failed:', error);
        setSearchCompanies([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    console.log('🔍 useEffect triggered:', {
      termListLength: termList.length,
      selectedTermId,
      isNewTerm,
      termList: termList.map(t => ({ id: t.id, title: t.title })),
    });

    // Auto-enter new term creation mode only when there are no terms and not already in new term mode
    if (termList.length === 0 && !isNewTerm) {
      console.log('📝 Creating new term (no terms available)');
      handleAddNewTerm(false); // Auto-create without message
      return;
    }

    // Select first term only when not a new term and selectedTermId is missing or not in termList
    if (
      !isNewTerm &&
      (!selectedTermId || selectedTermId < 0 || !termList.find(t => t.id === selectedTermId)) &&
      termList.length > 0
    ) {
      console.log('🎯 Selecting first term:', termList[0]);
      setSelectedTermId(termList[0].id);
    }
  }, [termList, isNewTerm, form, handleAddNewTerm]);

  useEffect(() => {
    console.log('🔄 Second useEffect triggered:', {
      selectedTermId,
      isNewTerm,
      contractId,
    });

    if (selectedTermId && selectedTermId > 0) {
      console.log('📥 Fetching term detail for ID:', selectedTermId);
      // Fetch data from API for existing term
      contractService.fetchTermDetail(contractId, selectedTermId).then(detail => {
        console.log('📋 Setting form values:', detail);
        form.setFieldsValue(detail);
        companyApiService.fetchLicensorById(detail.content_provider).then(licensorData => {
          // Add to search results to display as selected
          setSearchCompanies([licensorData]);
        });
      });
      setIsNewTerm(false);
    } else if (selectedTermId && selectedTermId < 0) {
      console.log('✨ New term mode (negative ID)');
      // New term case (negative ID)
      setIsNewTerm(true);
    } else {
      console.log('🧹 Resetting form (no term selected)');
      // No term selected case
      form.resetFields();
      setIsNewTerm(false);
    }
  }, [selectedTermId, contractId, form]);

  // labelCol/wrapperCol configuration
  const formItemLayout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 18 },
    labelAlign: 'left' as const,
  };

  // Wrap the area that changes per PaymentType in a Card
  const renderPaymentTypeBox = () => {
    return (
      <Card
        style={{ marginBottom: 12, borderRadius: 12, background: '#fafbfc' }}
        bodyStyle={{ padding: 24 }}
      >
        <Form.Item
          name="payment_type"
          label="Payment Type"
          required
          rules={[{ required: true, message: 'Please select a Payment Type.' }]}
        >
          <Select style={{ minWidth: 160 }}>
            {paymentTypeOptions.map(option => (
              <Select.Option key={option} value={option}>
                {option}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        {paymentType && paymentType !== 'FLAT_CREDIT' && (
          <Form.Item name="title_type" label="Title Type" style={{ marginBottom: 16 }}>
            <Radio.Group>
              <Radio value="SINGLE_TITLE" style={{ marginRight: 32 }}>
                Single Title
              </Radio>
              <Radio value="MULTI_TITLE" style={{ marginRight: 32 }}>
                Multi Title
              </Radio>
              <Radio value="CHANNEL">Channel</Radio>
            </Radio.Group>
          </Form.Item>
        )}
        {paymentType === 'RS_ONLY' && (
          <>
            <Typography.Text style={{ fontSize: 16, display: 'block', marginBottom: 8 }}>
              RS Rate
            </Typography.Text>
            <Row gutter={24}>
              <Col span={8}>
                <Form.Item
                  name="rs_rate_licensee"
                  label="Licensee"
                  required
                  rules={[{ required: true, message: 'Please enter Licensee RS Rate.' }]}
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                  style={{ marginBottom: 16 }}
                >
                  <CustomInputNumber
                    min={0}
                    max={100}
                    precision={2}
                    addonAfter="%"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="rs_rate_licensor"
                  label="Licensor"
                  required
                  rules={[{ required: true, message: 'Please enter Licensor RS Rate.' }]}
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                  style={{ marginBottom: 16 }}
                >
                  <CustomInputNumber
                    min={0}
                    max={100}
                    precision={2}
                    addonAfter="%"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="ops_cost_rate"
                  label="OPs Cost Rate"
                  required
                  rules={[{ required: true, message: 'Please enter OPs Cost Rate.' }]}
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  style={{ marginBottom: 16 }}
                >
                  <CustomInputNumber
                    min={0}
                    max={100}
                    precision={2}
                    addonAfter="%"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
        {paymentType === 'FLAT' && (
          <Form.Item
            name="output_deal"
            label="Output Deal"
            valuePropName="checked"
            labelCol={formItemLayout.labelCol}
            wrapperCol={formItemLayout.wrapperCol}
            style={{ marginBottom: 24 }}
          >
            <Switch />
          </Form.Item>
        )}
        {paymentType === 'FLAT' && (
          <Form.Item name="total_cost" label="Total Cost (Flat)" required>
            <CustomInputNumber min={0} precision={2} addonAfter="$" style={{ width: '200px' }} />
          </Form.Item>
        )}
        {paymentType === 'FLAT_CREDIT' && (
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="allowed_number_of_episodes"
                label="Allowed Number of Episode"
                required
                labelCol={{ span: 10 }}
                wrapperCol={{ span: 10 }}
                style={{ whiteSpace: 'normal' }}
              >
                <CustomInputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="price_per_episode"
                label="Price per Episode"
                required
                labelCol={{ span: 10 }}
                wrapperCol={{ span: 10 }}
                style={{ whiteSpace: 'normal' }}
              >
                <CustomInputNumber min={0} precision={2} addonAfter="$" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        )}
        {paymentType === 'MG_RS' && (
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="mg" label="MG" required>
                <CustomInputNumber min={0} precision={2} addonAfter="$" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ops_cost_rate" label="OPs Cost Rate" required>
                <CustomInputNumber
                  min={0}
                  max={100}
                  precision={2}
                  addonAfter="%"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
        {paymentType === 'MG_RS' && (
          <>
            <Typography.Text style={{ fontSize: 16, display: 'block', marginBottom: 8 }}>
              RS Rate
            </Typography.Text>
            <Row gutter={24}>
              <Col span={8}>
                <Form.Item
                  name="rs_rate_licensee"
                  label="Licensee"
                  required
                  rules={[{ required: true, message: 'Please enter Licensee RS Rate.' }]}
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                  style={{ marginBottom: 16 }}
                >
                  <CustomInputNumber
                    min={0}
                    max={100}
                    precision={2}
                    addonAfter="%"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="rs_rate_licensor"
                  label="Licensor"
                  required
                  rules={[{ required: true, message: 'Please enter Licensor RS Rate.' }]}
                  labelCol={{ span: 8 }}
                  wrapperCol={{ span: 16 }}
                  style={{ marginBottom: 16 }}
                >
                  <CustomInputNumber
                    min={0}
                    max={100}
                    precision={2}
                    addonAfter="%"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="ops_cost_rate"
                  label="OPs Cost Rate"
                  required
                  rules={[{ required: true, message: 'Please enter OPs Cost Rate.' }]}
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 14 }}
                  style={{ marginBottom: 16 }}
                >
                  <CustomInputNumber
                    min={0}
                    max={100}
                    precision={2}
                    addonAfter="%"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
        {(paymentType === 'RS_ONLY' || paymentType === 'MG_RS') && (
          <>
            <Form.Item name="report_frequency" label="Report Frequency">
              <Select placeholder="Select report frequency" allowClear>
                {reportFrequencyOptions.map(option => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="report_due_days" label="Report Due Days">
              <Select placeholder="Select report due days" allowClear>
                {reportDueDaysOptions.map(option => (
                  <Select.Option key={option} value={option}>
                    {option} days
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </>
        )}
      </Card>
    );
  };

  const handleOnClickButtonClick = async () => {
    try {
      // Form validation - throws error here if validation fails
      await form.validateFields();

      // Show loading message after validation passes
      message.loading(isNewTerm ? 'Creating term...' : 'Updating term...', 0);

      // Get form data
      const formData = form.getFieldsValue();

      if (isNewTerm) {
        // Create new term
        const newTerm = await contractService.createTerm(contractId, formData);
        message.destroy();
        message.success('Term created successfully');

        // Refresh term list
        if (onTermListRefresh) {
          await onTermListRefresh();
        }

        // Select newly created term
        setSelectedTermId(newTerm.id);
        setIsNewTerm(false);
      } else {
        // Update existing term
        if (!selectedTermId || selectedTermId < 0) {
          message.destroy();
          Modal.error({ title: 'Error', content: 'Please select a Term.', okText: 'Confirm' });
          return;
        }

        await contractService.updateTerm(contractId, selectedTermId, formData);
        message.destroy();
        message.success('Term updated successfully');

        // Refresh term list
        if (onTermListRefresh) {
          await onTermListRefresh();
        }
      }
    } catch (error) {
      message.destroy();

      // Validation error case
      if (error && typeof error === 'object' && 'errorFields' in error) {
        Modal.error({ title: 'Error', content: 'Please fill in all required fields.', okText: 'Confirm' });
        return;
      }

      // API error case
      Modal.error({ title: 'Error', content: `Failed to ${isNewTerm ? 'create' : 'update'} term: ` + error, okText: 'Confirm' });
    }
  };

  const handleDeleteTerm = async (termId: number) => {
    message.loading('Deleting term...', 0);
    try {
      await contractService.deleteTerm(contractId, termId);
      message.destroy();
      message.success('Term deleted successfully');

      // Refresh term list
      if (onTermListRefresh) {
        await onTermListRefresh();
      }

      // Deselect existing term
      setSelectedTermId(null);
      setIsNewTerm(false);
    } catch (error) {
      message.destroy();
      Modal.error({ title: 'Error', content: 'Failed to delete term: ' + error, okText: 'Confirm' });
    }
  };

  return (
    <>
      {/* Terms Header Section */}
      <div
        style={{
          marginBottom: 24,
          padding: '16px 0',
        }}
      >
        <Typography.Title level={4} style={{ margin: 0, color: '#262626' }}>
          Contract Terms
        </Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
          Manage contract terms
        </Typography.Text>
      </div>

      {/* Term Selection */}
      <div style={{ marginBottom: 24 }}>
        <Typography.Text
          strong
          style={{ fontSize: '14px', color: '#262626', marginBottom: 8, display: 'block' }}
        >
          {isNewTerm ? 'Editing New Term' : `Select Term (${termList.length})`}
        </Typography.Text>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Select
            value={selectedTermId && selectedTermId > 0 ? selectedTermId : undefined}
            onChange={value => {
              setSelectedTermId(value);
              setIsNewTerm(false);
            }}
            options={termList.map(term => ({ value: term.id, label: term.title }))}
            placeholder={isNewTerm ? 'Editing new Term...' : 'Choose a term to view and edit...'}
            style={{ width: '100%', maxWidth: 400 }}
            size="large"
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => handleAddNewTerm(true)}
          >
            Add New Term
          </Button>
          {selectedTermId && selectedTermId > 0 && !isNewTerm && (
            <Popconfirm
              title="Delete Term"
              description={
                <div>
                  <p>Are you sure you want to delete this Term?</p>
                  <p style={{ color: '#ff4d4f', fontSize: '12px', margin: 0 }}>
                    Deleted data cannot be recovered.
                  </p>
                </div>
              }
              onConfirm={() => handleDeleteTerm(selectedTermId)}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
              placement="topRight"
            >
              <Button danger icon={<DeleteOutlined />} size="large" title="Delete selected term">
                Delete
              </Button>
            </Popconfirm>
          )}
        </div>
      </div>

      {/* Divider */}
      <div
        style={{
          height: '1px',
          background: '#f0f0f0',
          margin: '32px 0',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            background: '#fff',
            padding: '0 16px',
            color: '#8c8c8c',
            fontSize: '12px',
          }}
        >
          Term Details
        </div>
      </div>

      {/* Term Details Form */}
      <Form
        form={form}
        layout="horizontal"
        labelCol={formItemLayout.labelCol}
        wrapperCol={formItemLayout.wrapperCol}
        labelAlign={formItemLayout.labelAlign}
        className="contract-term-details-form"
      >
        <Form.Item
          name="title"
          label="Term Name (Original Language)"
          required
          rules={[{ required: true, message: 'Please enter Term Name.' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="title_en"
          label="Term Name (EN)"
          required
          rules={[{ required: true, message: 'Please enter Term Name (EN).' }]}
        >
          <Input />
        </Form.Item>
        {renderPaymentTypeBox()}
        <Form.Item
          name="content_provider"
          label="Content Provider"
          rules={[{ required: true, message: 'Please select a licensor' }]}
        >
          <Select
            disabled={false}
            showSearch
            placeholder="Search and select content provider..."
            filterOption={false}
            onSearch={debouncedSearchCompanies}
            loading={searchLoading}
            notFoundContent={searchLoading ? 'Searching...' : 'No companies found'}
            style={{ width: '100%' }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          >
            {searchCompanies.map(renderCompanyOption)}
          </Select>
        </Form.Item>
        {/* Common fields below */}
        <Form.Item
          name="license_term"
          label="License Term (Month)"
          required
          rules={[{ required: true, message: 'Please enter License Term.' }]}
        >
          <CustomInputNumber min={0} />
        </Form.Item>
        <Row gutter={24} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <div style={{ display: 'flex', alignItems: 'center', height: 32 }}>
              <span style={{ marginRight: 8, fontWeight: 500 }}>Automatic Renewal</span>
              <Form.Item name="automatic_renewal" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
            </div>
          </Col>
          <Col span={8}>
            <div style={{ display: 'flex', alignItems: 'center', height: 32 }}>
              <span style={{ marginRight: 8, fontWeight: 500 }}>Exclusiveness</span>
              <Form.Item name="exclusiveness" valuePropName="checked" noStyle>
                <Switch />
              </Form.Item>
            </div>
          </Col>
        </Row>
        <Form.Item
          name="tax_type"
          label="Tax Type"
          required
          rules={[{ required: true, message: 'Please select a Tax Type.' }]}
        >
          <Select>
            {taxTypeOptions.map(option => (
              <Select.Option key={option} value={option}>
                {option}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="platforms"
          label="Service Platform"
          required
          rules={[{ required: true, message: 'Please select a Service Platform.' }]}
        >
          <Select mode="multiple">
            {platformsOptions.map(option => (
              <Select.Option key={option} value={option}>
                {option}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="original_audio_languages" label="Original Language">
          <Select
            mode="multiple"
            showSearch
            placeholder="Select a language"
            filterOption={(input, option) => {
              const searchTerm = input.toLowerCase();
              const language = LANGUAGE_OPTIONS.find(lang => lang.value === option?.value);
              if (!language) return false;

              const label = language.label.toLowerCase();
              const value = language.value.toLowerCase();

              return label.includes(searchTerm) || value.includes(searchTerm);
            }}
            style={{ width: '100%' }}
          >
            {LANGUAGE_OPTIONS.map(language => (
              <Select.Option key={language.value} value={language.value}>
                {language.value} - {language.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="subtitle_languages" label="Available Subtitle Language">
          <Select
            mode="multiple"
            showSearch
            placeholder="Select a language"
            filterOption={(input, option) => {
              const searchTerm = input.toLowerCase();
              const language = LANGUAGE_OPTIONS.find(lang => lang.value === option?.value);
              if (!language) return false;

              const label = language.label.toLowerCase();
              const value = language.value.toLowerCase();

              return label.includes(searchTerm) || value.includes(searchTerm);
            }}
            style={{ width: '100%' }}
          >
            {LANGUAGE_OPTIONS.map(language => (
              <Select.Option key={language.value} value={language.value}>
                {language.value} - {language.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="dubbing_languages" label="Available Dubbing Language">
          <Select
            mode="multiple"
            showSearch
            placeholder="Select a language"
            filterOption={(input, option) => {
              const searchTerm = input.toLowerCase();
              const language = LANGUAGE_OPTIONS.find(lang => lang.value === option?.value);
              if (!language) return false;

              const label = language.label.toLowerCase();
              const value = language.value.toLowerCase();

              return label.includes(searchTerm) || value.includes(searchTerm);
            }}
            style={{ width: '100%' }}
          >
            {LANGUAGE_OPTIONS.map(language => (
              <Select.Option key={language.value} value={language.value}>
                {language.value} - {language.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="payment_schedule"
          label="Payment Schedule"
        >
          <Input.TextArea />
        </Form.Item>
        <Form.Item name="payment_terms" label="Payment Terms">
          <Select placeholder="Select payment terms" allowClear>
            {paymentTermsOptions.map(option => (
              <Select.Option key={option} value={option}>
                {option.replace(/_/g, ' ')}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="sales_types"
          label="Allowed Right"
          required
          rules={[{ required: true, message: 'Please select an Allowed Right.' }]}
        >
          <Select mode="multiple">
            {salesTypeOptions.map(option => (
              <Select.Option key={option} value={option}>
                {salesTypeDisplayNames[option] || option}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="service_period_type"
          label="Service Period Type"
          required
          rules={[{ required: true, message: 'Please select a Service Period Type.' }]}
        >
          <Select>
            {servicePeriodTypeOptions.map(option => (
              <Select.Option key={option} value={option}>
                {option}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
      <ContentsMapping contractId={contractId} termId={selectedTermId} isNewTerm={isNewTerm} />
      <div className="people-detail-update-button">
        <Component.ColorButtonComponent text={'Save'} onClick={handleOnClickButtonClick} />
      </div>
    </>
  );
};

export default TermDetail;
